import { invariant } from '../util/invariant';
import { userPools, delegatedAuthStages, stageIds, type UserPoolInfo, customMimirHomeDomains, stageLocations, type StageLocation, productionAwsAccountIds } from './stageData';

/**
 * Type for valid values for the MIMIR_STAGE environment variable.
 */
export type MimirStage = typeof stageIds[number];

export function getMimirHomeDomain(stage: MimirStage): string {
  return customMimirHomeDomains[stage] ?? stage + '.mimir.tv';
}

export function getMimirHomeUrl(stage: MimirStage): string {
  return 'https://' + getMimirHomeDomain(stage);
}

export function deploymentPipelineNameForStage(stage: MimirStage): string {
  // The pipeline name is always `cd-$stage` now
  return 'cd-' + stage;
}

export function isMimirStage(value: unknown): value is MimirStage {
  const validIds: readonly unknown[] = stageIds;
  return validIds.includes(value);
}

export function getUserPoolInfo(stage: MimirStage): UserPoolInfo {
  const authStage = delegatedAuthStages[stage] ?? stage;
  const info = userPools[authStage];
  invariant(info, `No AWS user pool ID found for stage ${stage}`);
  return info;
}

export function stageDeploysAuth(stage: MimirStage) {
  return !(stage in delegatedAuthStages);
}

export function stageLocationsInAwsAccount(awsAccountId: string): Partial<Record<MimirStage, StageLocation>> {
  const relevantStages = Object.entries(stageLocations).filter(([_, location]) => location.awsAccountId == awsAccountId);
  return Object.fromEntries(relevantStages);
}

/** True if the stage runs in a production AWS account. */
export function isProductionStage(stage: MimirStage): boolean {
  return productionAwsAccountIds.includes(stageLocations[stage].awsAccountId);
}

export const productionStages: readonly MimirStage[] = stageIds.filter(isProductionStage);
